import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { VStack } from '@astryxdesign/core/VStack';
import { HStack } from '@astryxdesign/core/HStack';
import { Text } from '@astryxdesign/core/Text';
import { Heading } from '@astryxdesign/core/Heading';
import { Button } from '@astryxdesign/core/Button';
import { TextInput } from '@astryxdesign/core/TextInput';
import { TextArea } from '@astryxdesign/core/TextArea';
import { List, ListItem } from '@astryxdesign/core/List';
import { Banner } from '@astryxdesign/core/Banner';
import { Divider } from '@astryxdesign/core/Divider';
import { MetadataList, MetadataListItem } from '@astryxdesign/core/MetadataList';
import { useToast } from '@astryxdesign/core/Toast';
import { CaretRight, CheckCircle } from '@phosphor-icons/react';
import { Sheet, useBackButton } from '../../components/Sheet';
import { LoadingRows, ErrorView, Empty } from '../../components/StateViews';
import { useTenant } from '../../app/tenant-context';
import { publicApi, ApiError, type ClientBooking } from '../../lib/public-api';
import { attemptKey, clearAttempt, saveBooking } from '../../lib/my-bookings';
import { readJson, writeJson } from '../../lib/storage';
import { addDays, formatDateTime, formatDayShort, formatDuration, formatMoney, formatTime, isoWeekday, localDay, normalizePhone } from '../../../supabase/functions/_shared/domain/format.ts';
import { ReminderOffer } from './ReminderOffer';

type Step = 'service' | 'time' | 'contacts' | 'confirm' | 'done';
const ORDER: Step[] = ['service', 'time', 'contacts', 'confirm', 'done'];

interface Contacts { name: string; phone: string; car: string; car_plate: string; comment: string }
const EMPTY: Contacts = { name: '', phone: '', car: '', car_plate: '', comment: '' };

export function BookingSheet({ open, onClose, initialServiceId }: { open: boolean; onClose: () => void; initialServiceId?: string }) {
  const { slug, sf, base } = useTenant();
  const tz = sf.tenant.timezone;
  const qc = useQueryClient();
  const showToast = useToast();
  const [step, setStep] = useState<Step>('service');
  const [serviceId, setServiceId] = useState<string | undefined>(initialServiceId);
  const [day, setDay] = useState<string>(() => localDay(new Date(), tz));
  const [startsAt, setStartsAt] = useState<string | null>(null);
  const [contacts, setContacts] = useState<Contacts>(() => readJson(`studio:${slug}:contacts`, EMPTY));
  const [touched, setTouched] = useState(false);
  const [result, setResult] = useState<{ booking: ClientBooking; token: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setStartsAt(null);
    setTouched(false);
    setDay(localDay(new Date(), tz));
    setServiceId(initialServiceId);
    setStep(initialServiceId ? 'time' : 'service');
  }, [open, initialServiceId, tz]);

  const service = sf.services.find((s) => s.id === serviceId);
  const back = (): boolean => {
    const i = ORDER.indexOf(step);
    if (step === 'done' || i <= 0 || (step === 'time' && initialServiceId)) return false;
    setStep(ORDER[i - 1]!);
    return true;
  };
  useBackButton(open, back, onClose);

  // --- slots: one request per 7-day window -------------------------------
  const today = localDay(new Date(), tz);
  const windowStart = useMemo(() => {
    const diff = Math.floor((Date.parse(day) - Date.parse(today)) / 86_400_000);
    return addDays(today, Math.max(0, Math.floor(diff / 7) * 7));
  }, [day, today]);
  const slotsQ = useQuery({
    queryKey: ['slots', slug, serviceId, windowStart],
    queryFn: () => publicApi.slots(slug, serviceId!, windowStart, 7),
    enabled: open && step === 'time' && Boolean(serviceId),
    staleTime: 15_000,
  });
  const daySlots = (slotsQ.data ?? []).filter((s) => s.day === day);
  const openDays = new Set(sf.hours.map((h) => h.isodow));
  const days = Array.from({ length: Math.min(sf.profile.horizon_days + 1, 21) }, (_, i) => addDays(today, i));

  // --- contacts validation -----------------------------------------------
  const phone = normalizePhone(contacts.phone);
  const errors = {
    name: contacts.name.trim().length < 1 ? 'Укажите имя' : undefined,
    phone: !phone ? 'Телефон, например +7 999 123-45-67' : undefined,
    car: contacts.car.trim().length < 2 ? 'Марка и модель' : undefined,
  };
  const contactsValid = !errors.name && !errors.phone && !errors.car;

  // --- booking ------------------------------------------------------------
  const fingerprint = `${serviceId}|${startsAt}|${phone}`;
  const book = useMutation({
    mutationFn: () => publicApi.book({
      slug, service_id: serviceId!, starts_at: startsAt!, name: contacts.name.trim(), phone: phone!, car: contacts.car.trim(),
      car_plate: contacts.car_plate.trim(), comment: contacts.comment.trim(), idempotency_key: attemptKey(slug, fingerprint),
    }),
    retry: (n, e) => e instanceof ApiError && e.code === 'network' && n < 2,   // same key: safe to repeat
    onSuccess: (r) => {
      clearAttempt(slug, fingerprint);
      saveBooking(slug, { token: r.access_token, id: r.booking.id, starts_at: r.booking.starts_at, service_name: r.booking.service_name });
      writeJson(`studio:${slug}:contacts`, { ...contacts, comment: '' });
      setResult({ booking: r.booking, token: r.access_token });
      setStep('done');
      void qc.invalidateQueries({ queryKey: ['slots', slug] });
      void qc.invalidateQueries({ queryKey: ['my-bookings', slug] });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'slot_unavailable') {
        clearAttempt(slug, fingerprint);
        showToast({ body: e.message, type: 'error' });
        setStartsAt(null);
        setStep('time');
        void qc.invalidateQueries({ queryKey: ['slots', slug, serviceId] });
      }
    },
  });

  const titles: Record<Step, string> = {
    service: 'Запись в студию', time: 'Дата и время', contacts: 'Ваши данные', confirm: 'Проверьте запись', done: 'Вы записаны',
  };
  const stepNo = ORDER.indexOf(step) + 1;

  const footer = (() => {
    if (step === 'time') {
      return <Button label={startsAt ? `Дальше · ${formatTime(startsAt, tz)}` : 'Выберите время'} variant="primary" width="100%" size="lg"
        isDisabled={!startsAt} onClick={() => setStep('contacts')} />;
    }
    if (step === 'contacts') {
      return <Button label="Дальше" variant="primary" width="100%" size="lg"
        onClick={() => { setTouched(true); if (contactsValid) setStep('confirm'); }} />;
    }
    if (step === 'confirm') {
      return <Button label="Подтвердить запись" variant="primary" width="100%" size="lg" isLoading={book.isPending} onClick={() => book.mutate()} />;
    }
    if (step === 'done') return <Button label="Готово" variant="secondary" width="100%" size="lg" onClick={onClose} />;
    return null;
  })();

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={titles[step]}
      description={step !== 'done' ? `Шаг ${stepNo} из 4${service && step !== 'service' ? ` · ${service.name}` : ''}` : undefined}
      onBack={step !== 'service' && step !== 'done' && !(step === 'time' && initialServiceId) ? () => back() : undefined}
      stepKey={step}
      footer={footer}
    >
      {step === 'service' && (
        <List hasDividers density="spacious">
          {sf.services.map((s) => (
            <ListItem
              key={s.id}
              label={s.name}
              description={`${formatDuration(s.duration_minutes)} · ${formatMoney(s.price_cents, sf.tenant.currency, s.price_is_from)}`}
              endContent={<CaretRight size={18} aria-hidden />}
              onClick={() => { setServiceId(s.id); setStartsAt(null); setStep('time'); }}
            />
          ))}
        </List>
      )}

      {step === 'time' && service && (
        <VStack gap={4}>
          <div className="h-scroll" role="group" aria-label="Дата">
            {days.map((d) => {
              const closed = !openDays.has(isoWeekday(d)) || sf.closed_days.includes(d);
              const label = formatDayShort(`${d}T12:00:00Z`, 'UTC');
              return (
                <button key={d} type="button" className="chip" aria-pressed={d === day} disabled={closed}
                  onClick={() => { setDay(d); setStartsAt(null); }} aria-label={`${label}${closed ? ', выходной' : ''}`}>
                  <span className="chip-main">{label.split(',')[0]}</span>
                  <small>{label.split(',')[1]?.trim() ?? ''}</small>
                </button>
              );
            })}
          </div>
          {service.duration_minutes >= 1440 ? (
            <Banner status="info" title={`Машина остаётся в студии на ${formatDuration(service.duration_minutes)}`} container="card" />
          ) : null}
          {slotsQ.isPending ? <LoadingRows rows={2} height={48} label="Загружаем свободное время" /> :
           slotsQ.isError ? <ErrorView error={slotsQ.error} onRetry={() => slotsQ.refetch()} /> :
           daySlots.length === 0 ? <Empty title="В этот день приёма нет" description="Выберите другую дату." /> :
           !daySlots.some((s) => s.available) ? <Empty title="Всё занято" description="На этот день свободного времени нет — попробуйте соседние дни." /> : (
            <div className="slot-grid" role="group" aria-label="Время">
              {daySlots.map((s) => (
                <button key={s.starts_at} type="button" className="chip" disabled={!s.available} aria-pressed={startsAt === s.starts_at}
                  aria-label={`${formatTime(s.starts_at, tz)}${s.available ? '' : s.reason === 'busy' ? ', занято' : ', недоступно'}`}
                  onClick={() => setStartsAt(s.starts_at)}>
                  <span className="chip-main">{formatTime(s.starts_at, tz)}</span>
                  {!s.available ? <small>{s.reason === 'busy' ? 'занято' : 'поздно'}</small> : null}
                </button>
              ))}
            </div>
          )}
        </VStack>
      )}

      {step === 'contacts' && (
        <form onSubmit={(e) => { e.preventDefault(); setTouched(true); if (contactsValid) setStep('confirm'); }}>
          <VStack gap={3}>
            <TextInput label="Имя" value={contacts.name} autoComplete="name" enterKeyHint="next" isRequired
              onChange={(v) => setContacts({ ...contacts, name: v })} status={touched && errors.name ? { type: 'error', message: errors.name } : undefined} />
            <TextInput label="Телефон" type="tel" inputMode="tel" autoComplete="tel" value={contacts.phone} enterKeyHint="next" isRequired
              placeholder="+7 999 123-45-67" onChange={(v) => setContacts({ ...contacts, phone: v })}
              status={touched && errors.phone ? { type: 'error', message: errors.phone } : undefined} />
            <TextInput label="Автомобиль" value={contacts.car} placeholder="Например, Toyota Camry" enterKeyHint="next" isRequired
              onChange={(v) => setContacts({ ...contacts, car: v })} status={touched && errors.car ? { type: 'error', message: errors.car } : undefined} />
            <TextInput label="Госномер" value={contacts.car_plate} isOptional autoCapitalize="characters" enterKeyHint="next"
              onChange={(v) => setContacts({ ...contacts, car_plate: v })} />
            <TextArea label="Комментарий" value={contacts.comment} isOptional rows={2} maxLength={500}
              onChange={(v) => setContacts({ ...contacts, comment: v })} />
            <Text type="supporting">Регистрация не нужна. Телефон нужен студии, чтобы связаться с вами.</Text>
            <button type="submit" hidden aria-hidden tabIndex={-1} />
          </VStack>
        </form>
      )}

      {step === 'confirm' && service && startsAt && (
        <VStack gap={4}>
          <MetadataList columns="single">
            <MetadataListItem label="Услуга">{service.name}</MetadataListItem>
            <MetadataListItem label="Когда">{formatDateTime(startsAt, tz)}</MetadataListItem>
            <MetadataListItem label="Длительность">{formatDuration(service.duration_minutes)}</MetadataListItem>
            <MetadataListItem label="Стоимость">{formatMoney(service.price_cents, sf.tenant.currency, service.price_is_from)}</MetadataListItem>
            <MetadataListItem label="Адрес">{sf.profile.address}</MetadataListItem>
            <MetadataListItem label="Контакты">{`${contacts.name}, ${phone}`}</MetadataListItem>
            <MetadataListItem label="Автомобиль">{[contacts.car, contacts.car_plate].filter(Boolean).join(', ')}</MetadataListItem>
          </MetadataList>
          <Text type="supporting">Отменить онлайн можно не позднее чем за {sf.profile.cancel_min_hours_before} ч до записи.</Text>
          {sf.tenant.status === 'preview' ? <Banner status="warning" title="Это демонстрационная версия студии" description="Запись сохранится как пример и не будет обработана." container="card" /> : null}
          {book.isError && !(book.error instanceof ApiError && book.error.code === 'slot_unavailable') ? (
            <Banner status="error" title={book.error.message} description="Нажмите «Подтвердить запись» ещё раз — повторная отправка не создаст дубль." container="card" />
          ) : null}
        </VStack>
      )}

      {step === 'done' && result && (
        <VStack gap={4}>
          <HStack gap={3} vAlign="center">
            <CheckCircle size={40} weight="fill" color="var(--color-accent)" aria-hidden />
            <VStack gap={0.5}>
              <Heading level={3}>{result.booking.service_name}</Heading>
              <Text type="supporting">{formatDateTime(result.booking.starts_at, tz)} · код {result.booking.public_code}</Text>
            </VStack>
          </HStack>
          <Text>{sf.profile.address}</Text>
          <Divider />
          <ReminderOffer booking={result.booking} token={result.token} />
          <Link to={`${base}my`} onClick={onClose} className="text-accent underline underline-offset-4">Открыть «Моя запись»</Link>
        </VStack>
      )}
    </Sheet>
  );
}
